<script setup>
import { reactive, computed } from 'vue'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

// Props definition
const props = defineProps({
    slides: {
        type: Array,
        required: true,
        default: () => []
    },
    autoplay: {
        type: Boolean,
        default: true
    },
    autoplayInterval: {
        type: Number,
        default: 3000
    },
    loop: {
        type: Boolean,
        default: true
    },
    arrows: {
        type: Boolean,
        default: true
    },
    pagination: {
        type: Boolean,
        default: true
    },
    perPage: {
        type: Number,
        default: 1
    },
    gap: {
        type: String,
        default: '1rem'
    },
    speed: {
        type: Number,
        default: 600
    },
    breakpoints: {
        type: Object,
        default: () => ({})
    },
    type: {
        type: String,
        default: 'slide', // 'slide', 'loop', or 'fade'
        validator: (value) => ['slide', 'loop', 'fade'].includes(value)
    },
    dragFree: {
        type: Boolean,
        default: false
    },
    pauseOnHover: {
        type: Boolean,
        default: true
    },
    pauseOnFocus: {
        type: Boolean,
        default: true
    },
    keyboard: {
        type: Boolean,
        default: true
    },
    lazyLoad: {
        type: [Boolean, String],
        default: false
    },
    perMove: {
        type: [Number, String],
        default: 1,
        validator: (value) => {
            // Allow numbers or 'page' string
            return typeof value === 'number' || value === 'page'
        }
    }
})

// Carousel configuration
const state = reactive({
    options: computed(() => {
        // Default responsive breakpoints
        const defaultBreakpoints = {
            768: {
                // Below md (tablet and mobile)
                perPage: props.perPage > 1 ? Math.max(1, Math.floor(props.perPage / 2)) : 0.5,
                gap: '0.75rem',
                arrows: props.arrows,
                pagination: true
            },
            576: {
                // Below sm (mobile)
                perPage: 1, // Show half image for preview effect
                gap: '0.5rem',
                arrows: false, // Hide arrows on very small screens
                pagination: true
            }
        }

        // Merge user breakpoints with defaults (user breakpoints take priority)
        const mergedBreakpoints = {
            ...defaultBreakpoints,
            ...props.breakpoints
        }

        return {
            type: props.type,
            rewind: props.loop,
            perPage: props.perPage,
            perMove: props.perMove,
            gap: props.gap,
            speed: props.speed,
            arrows: props.arrows,
            pagination: props.pagination,
            autoplay: props.autoplay,
            interval: props.autoplayInterval,
            pauseOnHover: props.pauseOnHover,
            pauseOnFocus: props.pauseOnFocus,
            keyboard: props.keyboard,
            drag: true,
            dragFree: props.dragFree,
            lazyLoad: props.lazyLoad,
            breakpoints: mergedBreakpoints,
            ariaLabel: 'Carousel Navigation',
            ariaLabelledby: 'carousel-heading',
            classes: {
                arrows: 'splide__arrows carousel-arrows',
                arrow: 'splide__arrow carousel-arrow',
                prev: 'splide__arrow--prev carousel-arrow-prev',
                next: 'splide__arrow--next carousel-arrow-next',
                pagination: 'splide__pagination carousel-pagination',
                page: 'splide__pagination__page carousel-pagination-page',
            }
        }
    })
})

// Helper function to determine if slide has a link
const hasLink = (slide) => {
    return slide.link && slide.link.trim() !== ''
}

// Helper function to get target attribute
const getLinkTarget = (slide) => {
    return slide.newTab !== false ? '_blank' : '_self'
}

// Helper function to get rel attribute for external links
const getLinkRel = (slide) => {
    return slide.newTab !== false ? 'noopener noreferrer' : null
}
</script>

<template>
    <div class="general-carousel">
        <Splide :options="state.options" :has-track="false" aria-label="Image carousel">
            <div class="carousel-inner">
                <SplideTrack>
                    <SplideSlide v-for="(slide, index) in slides" :key="index">
                        <!-- Slide with link -->
                        <a
                            v-if="hasLink(slide)"
                            :href="slide.link"
                            :target="getLinkTarget(slide)"
                            :rel="getLinkRel(slide)"
                            class="carousel-slide-link"
                            :aria-label="slide.alt || slide.title || `Slide ${index + 1}`"
                        >
                            <div class="carousel-slide">
                                <!-- Image -->
                                <figure v-if="slide.image" class="carousel-slide-figure">
                                    <img
                                        :src="slide.image"
                                        :alt="slide.alt || slide.title || `Slide ${index + 1}`"
                                        :data-splide-lazy="lazyLoad ? slide.image : null"
                                        class="carousel-slide-image"
                                        loading="lazy"
                                    />
                                </figure>

                                <!-- Content overlay -->
<!--                                <div v-if="slide.title || slide.description" class="carousel-slide-content">-->
<!--                                    <h3 v-if="slide.title" class="carousel-slide-title">{{ slide.title }}</h3>-->
<!--                                    <p v-if="slide.description" class="carousel-slide-description">{{ slide.description }}</p>-->
<!--                                </div>-->
                            </div>
                        </a>

                        <!-- Slide without link -->
                        <div v-else class="carousel-slide">
                            <!-- Image -->
                            <figure v-if="slide.image" class="carousel-slide-figure">
                                <img
                                    :src="slide.image"
                                    :alt="slide.alt || slide.title || `Slide ${index + 1}`"
                                    :data-splide-lazy="lazyLoad ? slide.image : null"
                                    class="carousel-slide-image"
                                    loading="lazy"
                                />
                            </figure>

                            <!-- Content overlay -->
<!--                            <div v-if="slide.title || slide.description" class="carousel-slide-content">-->
<!--                                <h3 v-if="slide.title" class="carousel-slide-title">{{ slide.title }}</h3>-->
<!--                                <p v-if="slide.description" class="carousel-slide-description">{{ slide.description }}</p>-->
<!--                            </div>-->
                        </div>
                    </SplideSlide>
                </SplideTrack>
            </div>

            <!-- Custom arrows (if enabled) -->
            <div v-if="arrows" class="splide__arrows">
                <button class="splide__arrow splide__arrow--prev" aria-label="Previous slide">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.6349 7.88086L10.5238 13.992L4.41267 7.88086" stroke="currentColor" stroke-linecap="square"/>
                    </svg>
                </button>
                <button class="splide__arrow splide__arrow--next" aria-label="Next slide">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.6349 7.88086L10.5238 13.992L4.41267 7.88086" stroke="currentColor" stroke-linecap="square"/>
                    </svg>
                </button>
            </div>
        </Splide>
    </div>
</template>

<style scoped>
.general-carousel {
    position: relative;
    width: 100%;
    padding: 4rem 0;
}

.carousel-inner {
    position: relative;
}

.carousel-slide {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.carousel-slide-link {
    display: block;
    text-decoration: none;
    color: inherit;
    width: 100%;
    height: 100%;
    transition: opacity 0.3s ease;
}

.carousel-slide-link:hover {
    opacity: 0.9;
}

.carousel-slide-link:focus {
    outline: 2px solid #D62828;
    //outline-offset: 2px;
}

.carousel-slide-figure {
    margin: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: 300px;
}

.carousel-slide-image {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
    max-height: 100%;
    padding: 0 20px;
}

.carousel-slide-content {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 2rem;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
    color: white;
    z-index: 1;
}

.carousel-slide-title {
    margin: 0 0 0.5rem 0;
    font-size: 1.5rem;
    font-weight: bold;
}

.carousel-slide-description {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5;
}

/* Custom arrow styles */
:deep(.splide__arrows) {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    display: flex;
    justify-content: space-between;
    pointer-events: none;
    z-index: 10;
}

:deep(.splide__arrow) {
    position: relative;
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 50%;
    width: 3rem;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    pointer-events: auto;
    //box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(calc(50% - 1.5rem));
}

:deep(.splide__arrow:hover) {
    background: #e268683b;
    //box-shadow: 0 0 4px 12px rgba(0, 0, 0, 0.2);
}
:deep(.splide__arrow:hover svg path) {
    stroke: #D62828;
}

:deep(.splide__arrow:focus) {
    outline: 2px solid #D62828;
    //outline-offset: 2px;
}

:deep(.splide__arrow:disabled) {
    opacity: 0.3;
    cursor: not-allowed;
}

:deep(.splide__arrow svg) {
    width: 1.5rem;
    height: 1.5rem;
    color: #333;
}

:deep(.splide__arrow--prev) {
    left: 1rem;
}
:deep(.splide__arrow--prev svg) {
    transform: rotate(90deg);
}
:deep(.splide__arrow--prev svg path) {
    stroke: #4a4949;
    fill: none;
}

:deep(.splide__arrow--next) {
    right: 1rem;
}
:deep(.splide__arrow--next svg) {
    transform: rotate(-90deg);
}
:deep(.splide__arrow--next svg path) {
    stroke: #4a4949;
    fill: none;
}

/* Pagination styles */
:deep(.splide__pagination) {
    position: relative;
    bottom: auto;
    left: auto;
    transform: none;
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 2rem;
    padding-bottom: 0.5rem;
    z-index: 10;
}

:deep(.splide__pagination__page) {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
    border: 1px solid #D62828;
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 0;
}

:deep(.splide__pagination__page:hover) {
    background: rgba(255, 255, 255, 0.8);
    transform: scale(1.2);
}

:deep(.splide__pagination__page.is-active) {
    background: #D62828;
    transform: scale(1.3);
}

:deep(.splide__pagination__page:focus) {
    outline: 2px solid #D62828;
    //outline-offset: 2px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .carousel-slide-content {
        padding: 1rem;
    }

    .carousel-slide-title {
        font-size: 1.25rem;
    }

    .carousel-slide-description {
        font-size: 0.875rem;
    }

    :deep(.splide__arrow) {
        width: 2.5rem;
        height: 2.5rem;
    }

    :deep(.splide__arrow svg) {
        width: 1.25rem;
        height: 1.25rem;
    }

    :deep(.splide__arrow--prev) {
        left: 0.5rem;
    }

    :deep(.splide__arrow--next) {
        right: 0.5rem;
    }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
    .carousel-slide-link,
    :deep(.splide__arrow),
    :deep(.splide__pagination__page) {
        transition: none;
    }
}
</style>

